import { useEffect, useRef, useState } from 'react';
import { getPlans, type PlanEffort, type PlanModel } from '../../lib/conversationsApi';
import './modelSelector.css';

interface ModelSelectorProps {
  modelId: string;
  effortId: string;
  onChange: (next: { modelId: string; effortId: string }) => void;
  disabled?: boolean;
}

const AUTO_MODEL: PlanModel = { id: 'auto', label: 'Auto', provider: 'auto', tier: 'basic', creditCost: 0 };

// Ports ModelSelector.dc.html's "collapse model + effort into one control"
// idea, but the slider only has as many stops as config.json actually
// defines efforts for (quick/balanced/deep) -- the mockup's illustrative
// fourth "Max" stop has no backend effort behind it, so it's not rendered.
export function ModelSelector({ modelId, effortId, onChange, disabled }: ModelSelectorProps) {
  const [open, setOpen] = useState(false);
  const [models, setModels] = useState<PlanModel[]>([AUTO_MODEL]);
  const [efforts, setEfforts] = useState<PlanEffort[]>([]);
  const [changingModel, setChangingModel] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getPlans()
      .then((catalogue) => {
        setModels([AUTO_MODEL, ...catalogue.models]);
        setEfforts(catalogue.efforts);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
        setChangingModel(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const currentModel = models.find((m) => m.id === modelId) ?? AUTO_MODEL;
  const effortIndex = Math.max(0, efforts.findIndex((e) => e.id === effortId));
  const currentEffort = efforts[effortIndex];

  return (
    <div className="ozira-model-selector" ref={rootRef}>
      <button
        type="button"
        className="ozira-model-selector__pill"
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
      >
        {currentModel.label}
        {currentEffort ? ` · ${currentEffort.labelEn}` : ''}
        <span aria-hidden="true" className="ozira-model-selector__chevron">▾</span>
      </button>

      {open ? (
        <div className="ozira-model-selector__panel" role="dialog" aria-label="Model and effort">
          {changingModel ? (
            <div className="ozira-model-selector__model-list" role="listbox" aria-label="Choose model">
              {models.map((model) => (
                <button
                  key={model.id}
                  type="button"
                  role="option"
                  aria-selected={model.id === modelId}
                  className={`ozira-model-selector__model-option${model.id === modelId ? ' is-selected' : ''}`}
                  onClick={() => {
                    onChange({ modelId: model.id, effortId });
                    setChangingModel(false);
                  }}
                >
                  <span>{model.label}</span>
                  {model.tier === 'pro' ? <span className="ozira-model-selector__badge">Pro</span> : null}
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="ozira-model-selector__header">
                <span className="ozira-model-selector__model-name">{currentModel.label}</span>
                <button type="button" className="ozira-model-selector__change" onClick={() => setChangingModel(true)}>
                  Change model
                </button>
              </div>

              {efforts.length > 0 ? (
                <div className="ozira-model-selector__slider-wrap">
                  <input
                    type="range"
                    className="ozira-model-selector__slider"
                    min={0}
                    max={efforts.length - 1}
                    step={1}
                    value={effortIndex}
                    aria-label="Reasoning effort"
                    onChange={(e) => onChange({ modelId, effortId: efforts[Number(e.target.value)]?.id ?? effortId })}
                  />
                  <div className="ozira-model-selector__slider-labels">
                    {efforts.map((effort) => (
                      <span key={effort.id} className={effort.id === effortId ? 'is-active' : ''}>
                        {effort.labelEn}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {currentEffort && currentEffort.creditMultiplier !== 1 ? (
                <p className="ozira-model-selector__note">
                  {currentEffort.creditMultiplier > 1
                    ? `Uses ${currentEffort.creditMultiplier}× the usual credits for a deeper, more thorough answer.`
                    : `Uses ${currentEffort.creditMultiplier}× the usual credits for a faster, shorter answer.`}
                </p>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
